'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Menu, X, Zap } from 'lucide-react';

const navLinks = [
  { label: 'Home', href: '/', isRoute: true },
  { label: 'See AgentOS in Action', href: '/in-action', isRoute: true },
  { label: 'How It Works', href: '#how-it-works', isRoute: false },
  { label: 'Pricing', href: '#pricing', isRoute: false },
  { label: 'Resources', href: '#faq', isRoute: false },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string, isRoute: boolean) => {
    setMobileOpen(false);
    if (isRoute) {
      router.push(href);
      return;
    }
    // Hash link — if on homepage scroll to section, else navigate home then scroll
    if (pathname === '/') {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      router.push(`/${href}`);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl' : 'bg-transparent'
      }`}
      style={scrolled ? {
        background: 'rgba(11,15,25,0.90)',
        borderBottom: '1px solid rgba(255,255,255,0.08)',
        boxShadow: '0 4px 24px rgba(0,0,0,0.3)',
      } : {}}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2.5">
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{
                background: 'linear-gradient(135deg, #00D4FF, #0099CC)',
                boxShadow: '0 4px 12px rgba(0,212,255,0.35)',
              }}
            >
              <Zap size={16} className="text-black" strokeWidth={2.5} />
            </div>
            <span className="font-extrabold text-lg tracking-tight" style={{ color: '#F0F4FF' }}>AgentOS</span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href, link.isRoute)}
                className="nav-link"
                style={
                  (link.isRoute && pathname === link.href)
                    ? { color: '#00D4FF' }
                    : {}
                }
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">
            <a
              href="https://agentos-ai-vgojf93.public.builtwithrocket.new/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary text-sm py-2 px-5"
            >
              Login
            </a>
            <a
              href="https://agentos-ai-vgojf93.public.builtwithrocket.new/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary text-sm py-2 px-5"
            >
              Request Early Access
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg transition-colors"
            style={{ color: 'rgba(240,244,255,0.7)', background: 'rgba(255,255,255,0.06)' }}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div
          className="lg:hidden backdrop-blur-xl"
          style={{
            background: 'rgba(11,15,25,0.95)',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}
        >
          <div className="px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href, link.isRoute)}
                className="block w-full text-left px-3 py-2.5 text-sm font-medium rounded-lg transition-colors"
                style={{
                  color: (link.isRoute && pathname === link.href) ? '#00D4FF' : 'rgba(240,244,255,0.65)',
                  background: (link.isRoute && pathname === link.href) ? 'rgba(0,212,255,0.08)' : 'transparent',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.color = '#00D4FF';
                  (e.currentTarget as HTMLElement).style.background = 'rgba(0,212,255,0.08)';
                }}
                onMouseLeave={(e) => {
                  if (!(link.isRoute && pathname === link.href)) {
                    (e.currentTarget as HTMLElement).style.color = 'rgba(240,244,255,0.65)';
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                  }
                }}
              >
                {link.label}
              </button>
            ))}
            <div className="pt-3 flex flex-col gap-2">
              <a
                href="https://agentos-ai-vgojf93.public.builtwithrocket.new/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary text-sm text-center"
              >
                Login
              </a>
              <a
                href="https://agentos-ai-vgojf93.public.builtwithrocket.new/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-sm text-center"
              >
                Request Early Access
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
